// initialized from https://ui.shadcn.com/docs/components/button
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import cx from "classnames";
import { useAnimate, useMotionValue, motion, MotionValue } from "motion/react";
import React, {
  isValidElement,
  ReactNode,
  ButtonHTMLAttributes,
  forwardRef,
  cloneElement,
  Children,
  useCallback,
  useMemo,
} from "react";
import { animationCurves } from "@utils/animationCurves";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 transition-all disabled:!scale-100 focus-visible:scale-105 hover:scale-105 active:scale-95 relative disabled:bg-neutral-cream/15",
  {
    variants: {
      variant: {
        text: "",
        "light-on-true": "bg-palette-current-true rounded-lg text-neutral-light",
        "light-on-blur": "text-neutral-light rounded-lg bg-neutral-light/20 backdrop-blur-sm",
        "light-on-dark": "text-neutral-light rounded-lg bg-palette-current-dark",
        "current-on-blur": "text-palette-current-foreground bg-neutral-light/20 backdrop-blur-sm",
        "dark-on-light": "bg-neutral-light text-neutral-dark rounded-lg",
        "highlight-on-deep": "bg-palette-current-deep text-palette-current-highlight",
        "red-mx": "bg-[#E50914] text-[#181616] rounded-none border-[5px]",
      },
      size: {
        inherit: "",
        default: "text-meta p-3",
        small: "text-button-small p-3",
        large:
          "text-button-large rounded-lg px-[22px] py-[14px] text-button-large normal-case",
      },
    },
    defaultVariants: {
      size: "large",
      variant: "light-on-blur",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      disabled: disabledProp,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const disabled = disabledProp || loading;

    const y = useMotionValue("0%");
    const [, animate] = useAnimate();

    const {
      onMouseEnter,
      onMouseLeave,
      "aria-expanded": ariaExpanded,
      "aria-selected": ariaSelected,
    } = props;
    const marked = ariaExpanded || ariaSelected || disabled;
    const animateForward = useCallback(async () => {
      if (marked) return;
      await animate(y, "100%", animationCurves.quart);
      y.set("-100%");
      animate(y, "0%", animationCurves.half);
    }, [animate, y, marked]);

    const animateBackward = useCallback(async () => {
      if (marked) return;
      await animate(y, "-100%", animationCurves.quart);
      y.set("100%");
      animate(y, "0%", animationCurves.half);
    }, [animate, y, marked]);

    const preventableAnimationEventHandlers = useMemo(
      () => ({
        onMouseEnter: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onMouseEnter?.(e);
          if (e.defaultPrevented) return;
          animateForward();
        },
        onMouseLeave: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onMouseLeave?.(e);
          if (e.defaultPrevented) return;
          animateBackward();
        },
      }),
      [animateForward, animateBackward, onMouseEnter, onMouseLeave]
    );

    if (asChild && loading !== undefined) {
      console.error(
        "Button: The loading & asChild props are not supported together"
      );
    }

    return (
      <Comp
        className={cx(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={props.type == null && Comp === "button" ? "button" : undefined}
        disabled={disabled}
        {...props}
        {...preventableAnimationEventHandlers}
      >
        {asChild ? (
          textNodesToRollingEffect(children, y)
        ) : (
          <span>
            <span
              className={cx(
                "transition-opacity absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none size-full flex items-center justify-center opacity-0",
                loading && "opacity-100"
              )}
            >
              <span
                className="animate-spin h-1/2 aspect-square rounded-full"
                style={{
                  borderBlockStart: "2px solid currentcolor",
                  borderInlineEnd: "2px solid transparent",
                }}
              ></span>
            </span>
            <span
              className={cx(
                "transition-opacity flex gap-2 items-center",
                loading && "opacity-0"
              )}
            >
              {textNodesToRollingEffect(children, y)}
            </span>
          </span>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

// This can't be a component because that would break the `asChild` prop of the button
const textNodesToRollingEffect = (
  children: ReactNode,
  y: MotionValue<string>
) => {
  const wrapText = (node: ReactNode): ReactNode => {
    const nodeIsSvg =
      isValidElement(node) &&
      node.type === "svg" &&
      !node.props["data-no-roll-animate"];
    if (typeof node === "string" || typeof node === "number" || nodeIsSvg) {
      return (
        <span className="overflow-hidden inline-flex items-center">
          <motion.span style={{ y }} className="inline-flex">
            {node}
          </motion.span>
        </span>
      );
    }

    if (isValidElement(node)) {
      return cloneElement(
        node,
        node.props,
        Children.map(node.props.children, wrapText)
      );
    }
    return node;
  };

  if (Children.count(children) === 1) {
    return wrapText(children);
  } else return Children.map(Children.toArray(children), wrapText);
};

export { Button };
